<script setup lang="ts">
  import { ref } from 'vue'
  import { useAppConfig } from 'nuxt/app'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import useOutbrainAds, {
    getAdBrandFromElement,
    subscribeToOutbrain,
  } from '../../composables/ad-ops/useOutbrainAds'
  import {
    useNativeAd,
    type NativeAdRender,
  } from '../../composables/ad-ops/useNativeAd'
  import { createExtrnId } from '../../utils/string/uuid'

  interface OutbrainWidgetAttributes {
    'data-ob-portalUrl': string
    'data-ob-language': string
    'data-widget-id': string
    'data-external-id': string
    'data-ob-installation-key': string
    'data-external-secondary-id': string
    'data-render-registered': boolean
  }

  /**
   * Props for the Outbrain component
   * @property placement - The placement identifier for the ad
   * @property widgetId - The Outbrain widget ID
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface OutbrainProps {
    placement: string
    widgetId: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
    isFeed?: boolean
    ayInstanceCode?: string
    adRefresh?: boolean
  }

  const props = withDefaults(defineProps<OutbrainProps>(), {
    tilePosition: -1,
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
    isFeed: false,
    ayInstanceCode: '',
  })

  if (!props.placement || !props.widgetId) {
    throw new Error(
      `Outbrain component requires placement and widgetId props- ${props.placement}`
    )
  }

  const outbrain = useAppConfig()?.adops?.outbrain ?? {}

  const { renderWidget, generateExternalSecondaryId } = useOutbrainAds()

  const placementClassName = ref<string>(props.placement)
  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')
  const widgetEl = useTemplateRef<HTMLElement | null>('widgetEl')

  const widgetAttributes = ref<OutbrainWidgetAttributes>({
    'data-ob-portalUrl': '',
    'data-ob-language': 'en',
    'data-widget-id': props.widgetId,
    'data-external-id': '',
    'data-ob-installation-key': outbrain?.installationKey || '',
    'data-external-secondary-id': createExtrnId(),
    'data-render-registered': false,
  })

  if (import.meta.client) {
    widgetAttributes.value['data-ob-portalUrl'] = getTruncatedUrl()
    widgetAttributes.value['data-external-id'] = window.location.host
    widgetAttributes.value['data-external-secondary-id'] =
      generateExternalSecondaryId()
  }

  function eventData(brand: string): NtpAdEvent {
    return {
      ntp_tile_position: props.tilePosition,
      ntp_ad_provider: 'outbrain',
      ntp_creative_id: props.widgetId,
      ntp_placement_name: props.placement,
      ntp_ad_brand: brand,
      ntp_extern_id: widgetAttributes.value['data-external-id'],
      ntp_extern_secondary_id:
        widgetAttributes.value['data-external-secondary-id'],
      ...(props.ayInstanceCode && {
        ntp_ay_instance_code: props.ayInstanceCode,
        ntp_ad_refresh: props.adRefresh,
      }),
    }
  }

  // A feed's NtpAd_Rendered uses the brand of its first loaded tile.
  function firstFeedTileBrand(): string {
    const cards = widgetWrapperEl.value?.querySelectorAll(
      '.OUTBRAIN[data-card-idx]'
    )
    for (const card of cards ?? []) {
      const widget =
        card.shadowRoot?.querySelector('.ob-widget[class*="FMS"]') ??
        card.querySelector('.ob-widget[class*="FMS"]')
      const tile = widget
        ?.querySelector('.ob-widget-items-container')
        ?.querySelector('.ob-dynamic-rec-container, .ob-rec-box')
      if (tile) return getAdBrandFromElement(tile)
    }
    return ''
  }

  const { showWidget } = useNativeAd({
    provider: 'outbrain',
    props,
    wrapper: widgetWrapperEl,
    subscribe: (handlers) =>
      subscribeToOutbrain(
        props.widgetId,
        (element) =>
          element === widgetEl.value ||
          Boolean(widgetWrapperEl.value?.contains(element)),
        handlers
      ),
    clickSlotId: props.widgetId,
    eventData,
    tileBrand: getAdBrandFromElement,
    renderBrand: ({ element }: NativeAdRender) =>
      (props.isFeed && firstFeedTileBrand()) || getAdBrandFromElement(element),
    nameInstance: (index) => {
      placementClassName.value =
        index > 0
          ? `${props.placement} ${props.placement}-${index}`
          : props.placement
    },
    request: renderWidget,
  })
</script>

<template>
  <div
    ref="widgetWrapperEl"
    :class="placementClassName"
    :data-ntp-extern-id="widgetAttributes['data-external-id']"
    :data-ntp-extern-secondary-id="
      widgetAttributes['data-external-secondary-id']
    "
    :data-ad-instance-id="props.adInstanceId"
  >
    <div
      v-if="showWidget"
      ref="widgetEl"
      class="OUTBRAIN flex items-center justify-center"
      :class="placementClassName"
      v-bind="widgetAttributes"
    />
  </div>
</template>
